import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Bot, Server, Key, Shield, AlertTriangle, 
  Clock, Activity, Power, FileText, Network, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getAgent, executeKillSwitch, reactivateAgent } from '../services/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const AgentDetailPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAgent();
  }, [agentId]);

  const fetchAgent = async () => {
    try {
      const response = await getAgent(agentId);
      setAgent(response.data);
    } catch (error) {
      console.error('Failed to fetch agent:', error);
      toast.error('Failed to load agent details');
    } finally {
      setLoading(false);
    }
  };

  const handleKillSwitch = async (actionType, reason) => {
    setActionLoading(true);
    try {
      await executeKillSwitch({
        agent_id: agentId,
        action_type: actionType,
        reason: reason
      });
      toast.success(`Action "${actionType}" executed successfully`);
      fetchAgent();
    } catch (error) {
      toast.error('Failed to execute action');
    }
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await reactivateAgent(agentId);
      toast.success('Agent reactivated');
      fetchAgent();
    } catch (error) {
      toast.error('Failed to reactivate agent');
    }
    setActionLoading(false);
  };

  const getRiskClass = (level) => {
    switch (level) {
      case 'critical': return 'risk-critical';
      case 'high': return 'risk-high';
      case 'medium': return 'risk-medium';
      default: return 'risk-low';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'disabled': return 'status-disabled';
      case 'blocked': return 'status-blocked';
      default: return 'text-[#FFCC00] bg-[#FFCC00]/10 px-2 py-0.5 rounded-none text-xs font-mono';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#9CA3AF]">Loading agent details...</div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="text-center py-12">
        <p className="text-[#9CA3AF]">Agent not found</p>
        <Button onClick={() => navigate('/dashboard')} className="mt-4 secondary-btn">
          Back to Inventory
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="agent-detail-page">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/dashboard/inventory')}
          className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F9FAFB] mb-4 transition-colors"
          data-testid="back-to-inventory-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Inventory
        </button>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {agent.name}
              </h1>
              <span className={getRiskClass(agent.risk_level)}>{agent.risk_level}</span>
              <span className={getStatusClass(agent.status)}>{agent.status}</span>
            </div>
            <p className="text-[#9CA3AF]">{agent.description || 'No description'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/dashboard/graph?agent=${agent.id}`}>
              <Button variant="outline" className="secondary-btn" data-testid="view-graph-btn">
                <Network className="w-4 h-4 mr-2" />
                View Graph
              </Button>
            </Link>
            {agent.status === 'active' ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="kill-switch-btn" data-testid="disable-agent-btn">
                    <Power className="w-4 h-4 mr-2" />
                    Disable Agent
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#F9FAFB]">Disable Agent?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9CA3AF]">
                      This will immediately disable the agent and prevent it from executing any actions.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="kill-switch-btn"
                      onClick={() => handleKillSwitch('agent_disabled', 'Manual disable from dashboard')}
                      disabled={actionLoading}
                    >
                      Confirm Disable
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button 
                onClick={handleReactivate} 
                className="primary-btn"
                disabled={actionLoading}
                data-testid="reactivate-agent-btn"
              >
                Reactivate Agent
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview Card */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4">Overview</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Type</div>
                <div className="text-[#F9FAFB] font-mono">{agent.type.replace('_', ' ')}</div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Environment</div>
                <div className="text-[#F9FAFB] font-mono">{agent.environment}</div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Framework</div>
                <div className="text-[#F9FAFB] font-mono">{agent.framework || 'N/A'}</div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Source</div>
                <div className="text-[#F9FAFB] font-mono">{agent.source_connector}</div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Owner</div>
                <div className={agent.owner ? 'text-[#F9FAFB]' : 'text-[#FF3B30]'}>
                  {agent.owner || 'No Owner Assigned'}
                </div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Owner Email</div>
                <div className="text-[#F9FAFB]">{agent.owner_email || 'N/A'}</div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Risk Score</div>
                <div className={`font-mono font-bold ${agent.risk_score > 70 ? 'text-[#FF3B30]' : agent.risk_score > 40 ? 'text-[#FFCC00]' : 'text-[#34C759]'}`}>
                  {agent.risk_score}/100
                </div>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs uppercase tracking-wider mb-1">Last Seen</div>
                <div className="text-[#F9FAFB] font-mono text-sm">
                  {agent.last_seen ? new Date(agent.last_seen).toLocaleString() : 'Never'}
                </div>
              </div>
            </div>
          </div>

          {/* Credentials Card */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-[#FFCC00]" />
              Credentials ({agent.credentials?.length || 0})
            </h2>
            {agent.credentials?.length > 0 ? (
              <div className="space-y-2">
                {agent.credentials.map((cred, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#1A1A1A] border border-[#27272A]/40">
                    <span className="font-mono text-sm text-[#F9FAFB]">{cred}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#6B7280]">No credentials associated</p>
            )}
          </div>

          {/* Tools Card */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-[#0066FF]" />
              Connected Tools ({agent.tools?.length || 0})
            </h2>
            {agent.tools?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {agent.tools.map((tool, i) => (
                  <span key={i} className="px-3 py-1 bg-[#0066FF]/10 border border-[#0066FF]/30 text-[#0066FF] font-mono text-sm">
                    {tool}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[#6B7280]">No tools connected</p>
            )}
          </div>

          {/* Resources Card */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Server className="w-5 h-5 text-[#34C759]" />
              Resources Accessed ({agent.resources_accessed?.length || 0})
            </h2>
            {agent.resources_accessed?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {agent.resources_accessed.map((resource, i) => (
                  <span key={i} className="px-3 py-1 bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] font-mono text-sm">
                    {resource}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[#6B7280]">No resources accessed</p>
            )}
          </div>

          {/* Recent Actions */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#9CA3AF]" />
              Recent Actions
            </h2>
            {agent.recent_actions?.length > 0 ? (
              <div className="space-y-3">
                {agent.recent_actions.map((action, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#1A1A1A] border border-[#27272A]/40">
                    <div>
                      <span className="font-mono text-sm text-[#F9FAFB]">{action.action}</span>
                      <span className="text-[#6B7280] mx-2">→</span>
                      <span className="font-mono text-sm text-[#9CA3AF]">{action.resource}</span>
                    </div>
                    <span className="text-xs text-[#6B7280]">
                      {new Date(action.timestamp).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#6B7280]">No recent actions</p>
            )}
          </div>
        </div>

        {/* Right Column - Actions & Permissions */}
        <div className="space-y-6">
          {/* Kill Switch Actions */}
          <div className="enterprise-card p-6 border-l-4 border-[#FF3B30]">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Power className="w-5 h-5 text-[#FF3B30]" />
              Kill Switch Actions
            </h2>
            <div className="space-y-3">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-[#FF3B30] border-[#FF3B30]/50 hover:bg-[#FF3B30]/10"
                    disabled={actionLoading || agent.status === 'disabled'}
                    data-testid="revoke-token-btn"
                  >
                    <Key className="w-4 h-4 mr-2" />
                    Revoke All Tokens
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#F9FAFB]">Revoke All Tokens?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9CA3AF]">
                      This will immediately revoke all credentials and tokens associated with this agent.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="kill-switch-btn"
                      onClick={() => handleKillSwitch('token_revoked', 'Manual token revocation')}
                    >
                      Revoke Tokens
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-[#FFCC00] border-[#FFCC00]/50 hover:bg-[#FFCC00]/10"
                    disabled={actionLoading}
                    data-testid="approval-mode-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Enable Approval Mode
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#F9FAFB]">Enable Approval Mode?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9CA3AF]">
                      All actions by this agent will require human approval before execution.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="primary-btn"
                      onClick={() => handleKillSwitch('approval_mode_enabled', 'Manual approval mode')}
                    >
                      Enable
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-[#9CA3AF] border-[#27272A] hover:bg-[#1A1A1A]"
                    disabled={actionLoading}
                    data-testid="block-tools-btn"
                  >
                    <Activity className="w-4 h-4 mr-2" />
                    Block All Tools
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[#F9FAFB]">Block All Tools?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[#9CA3AF]">
                      This will prevent the agent from accessing any connected tools.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="kill-switch-btn"
                      onClick={() => handleKillSwitch('tool_blocked', 'Manual tool blocking')}
                    >
                      Block Tools
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Permissions Card */}
          <div className="enterprise-card p-6">
            <h2 className="section-title mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0066FF]" />
              Effective Permissions ({agent.permissions?.length || 0})
            </h2>
            {agent.permissions?.length > 0 ? (
              <div className="space-y-2">
                {agent.permissions.map((perm, i) => (
                  <div key={i} className="py-2 px-3 bg-[#1A1A1A] border border-[#27272A]/40">
                    <span className="font-mono text-sm text-[#F9FAFB]">{perm}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#6B7280]">No permissions assigned</p>
            )}
          </div>

          {/* Metadata Card */}
          {agent.metadata && Object.keys(agent.metadata).length > 0 && (
            <div className="enterprise-card p-6">
              <h2 className="section-title mb-4">Metadata</h2>
              <div className="space-y-2">
                {Object.entries(agent.metadata).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 px-3 bg-[#1A1A1A] border border-[#27272A]/40">
                    <span className="text-[#6B7280] text-sm">{key}</span>
                    <span className="font-mono text-sm text-[#F9FAFB]">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDetailPage;
